<template>
    <div class="tw:bg-white tw:rounded-xl tw:shadow-sm tw:border tw:border-gray-100 tw:overflow-hidden">
        <div class="tw:bg-linear-to-r tw:from-blue-600 tw:to-blue-500 tw:px-6 tw:py-4">
            <div class="tw:flex tw:items-center tw:justify-between">
                <h3 class="tw:text-base tw:font-semibold tw:text-white tw:tracking-wide tw:flex tw:items-center tw:gap-2">
                    <i class="fas fa-id-card tw:opacity-80"></i>
                    Datos del abono
                </h3>
                <div v-if="headerOptions && headerOptions.length > 0">
                    <OptionsDropdownComponent
                        :options="headerOptions"
                        label="OPCIONES"
                        custom-class="tw:text-xs "
                    />
                </div>
            </div>
        </div>

        <div class="tw:p-6 tw:space-y-5">
            <div class="tw:bg-blue-50 tw:border tw:border-blue-100 tw:rounded-lg tw:p-4">
                <p class="tw:text-xs tw:font-semibold tw:text-blue-400 tw:uppercase tw:tracking-wider tw:mb-2 tw:flex tw:items-center tw:gap-1">
                    <i class="fas fa-map-marker-alt"></i>
                    {{ abonado.plaza ? 'Butaca' : 'Asiento' }}
                </p>
                <div class="tw:flex tw:items-center tw:justify-between tw:gap-3 tw:flex-wrap">
                    <p class="tw:text-sm tw:font-bold tw:text-blue-800 tw:m-0">
                        <template v-if="abonado.tiene_asiento">
                            {{ abonado.asiento.region }}, Fila: {{ abonado.asiento.row }}, Butaca: {{ abonado.asiento.seat }}
                        </template>
                        <template v-else-if="abonado.plaza">
                            {{ getNombreButaca(abonado.plaza) }}
                        </template>
                        <template v-else>
                            <span class="tw:text-blue-300 tw:font-normal">Sin asiento</span>
                        </template>
                    </p>
                    <Button
                        id="cambiarAsiento"
                        variant="primary"
                        class="tw:text-xs tw:shrink-0"
                        @click="openCambiarAsiento"
                    >
                        <i class="fas fa-exchange-alt tw:mr-1"></i> CAMBIAR
                    </Button>
                </div>
            </div>

            <div class="tw:bg-gray-50 tw:border tw:border-gray-200 tw:rounded-lg tw:p-4 tw:grid tw:grid-cols-2 tw:gap-4">
                <div>
                    <p class="tw:text-xs tw:font-semibold tw:text-gray-400 tw:uppercase tw:tracking-wider tw:mb-1 tw:flex tw:items-center tw:gap-1">
                        <i class="fas fa-tag"></i> Tipo de abono
                    </p>
                    <p class="tw:text-sm tw:font-bold tw:text-gray-800 tw:m-0">
                        <template v-if="abonado.es_internacional">Internacional</template>
                        <template v-else-if="abonado.precio != null">{{ abonado.precio.descripcion }}</template>
                        <template v-else>-</template>
                    </p>
                </div>
                <div>
                    <p class="tw:text-xs tw:font-semibold tw:text-gray-400 tw:uppercase tw:tracking-wider tw:mb-1 tw:flex tw:items-center tw:gap-1">
                        <i class="fas fa-euro-sign"></i> Precio
                    </p>
                    <p class="tw:text-sm tw:font-bold tw:text-blue-600 tw:m-0">
                        <template v-if="abonado.descuento != null">
                            {{ abonado.descuento.descripcion }} ({{ abonado.descuento.precio | formatear_precio }})
                        </template>
                        <template v-else>-</template>
                    </p>
                </div>
                <div
                    v-if="abonado.activo && abonado.transacciones && abonado.transacciones.length > 0 && abonado.transacciones[0].metodo_pago != 5 && ((abonado.plazos.length == 0 && !abonado.plazos_pagados) || (abonado.plazos.length > 0 && abonado.plazos_pagados))"
                    class="tw:col-span-2 tw:pt-3 tw:border-t tw:border-gray-200"
                >
                    <Button @click="openCambiarPrecio" class="tw:text-xs">
                        <i class="fas fa-pencil-alt tw:mr-1"></i> Cambiar tipo de abono y precio
                    </Button>
                </div>
            </div>

            <div class="tw:grid tw:grid-cols-2 tw:gap-3">
                <DataField 
                    :icon="abonado.n_abonado_antiguo ? 'fas fa-calendar-check' : 'fas fa-calendar-plus'"
                    :label="abonado.n_abonado_antiguo ? 'Fecha de renovación' : 'Fecha de alta'"
                    :value="abonado.fecha_alta"
                    labelClass="tw:text-amber-500"
                    valueClass="tw:text-amber-800"
                    wrapperClass="tw:bg-amber-50 tw:border tw:border-amber-100 tw:rounded-lg tw:p-3 tw:flex"
                />
                <DataField 
                    icon="fas fa-history"
                    label="Fecha de antigüedad"
                    :value="abonado.fecha_antiguedad"
                    labelClass="tw:text-amber-500"
                    valueClass="tw:text-amber-800"
                    wrapperClass="tw:bg-amber-50 tw:border tw:border-amber-100 tw:rounded-lg tw:p-3 tw:flex"
                />
            </div>

            <div class="tw:border-t tw:border-gray-100"></div>

            <div class="tw:space-y-3">
                <p class="tw:text-xs tw:font-semibold tw:text-gray-400 tw:uppercase tw:tracking-wider tw:flex tw:items-center tw:gap-1">
                    <i class="fas fa-info-circle"></i> Información adicional
                </p>

                <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">

                    <DataField 
                        icon="fas fa-comment-alt"
                        label="Observaciones"
                        :value="abonado.observaciones"
                        wrapperClass="tw:md:col-span-2"
                    />

                    <DataField 
                        icon="fas fa-university"
                        label="IBAN"
                        :value="abonado.iban"
                    />

                    <DataField 
                        icon="fas fa-user-circle"
                        label="Titular de la cuenta"
                        :value="abonado.titular_cuenta"
                    />

                    <DataField 
                        icon="fas fa-store"
                        label="Vendido por"
                        :value="vendidoPor"
                        wrapperClass="tw:md:col-span-2"
                    />

                    <DataField 
                        icon="fas fa-route"
                        label="Origen"
                        :value="abonado.origen"
                    />

                    <DataField 
                        icon="fas fa-barcode"
                        label="Código"
                        :value="abonado.footter"
                    />

                    <DataField 
                        icon="fas fa-qrcode"
                        label="Qr code"
                        :value="abonado.qr_code"
                    />

                </div>
            </div>

            <div class="tw:border-t tw:border-gray-100"></div>

            <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
                <div class="tw:space-y-2">
                    <p class="tw:text-xs tw:font-semibold tw:text-gray-400 tw:uppercase tw:tracking-wider tw:mb-2 tw:flex tw:items-center tw:gap-1">
                        <i class="fas fa-print"></i> Impresión
                    </p>
                    <div class="tw:flex tw:items-center tw:gap-3">
                        <span
                            :class="abonado.impresion && abonado.impresion.length > 0
                                ? 'tw:bg-green-100 tw:text-green-700'
                                : 'tw:bg-gray-100 tw:text-gray-500'"
                            class="tw:text-xs tw:font-semibold tw:rounded-full tw:px-3 tw:py-1"
                        >
                            <i class="fas fa-circle tw:mr-1" style="font-size:7px;vertical-align:middle;"></i>
                            {{ abonado.impresion && abonado.impresion.length > 0 ? 'SI' : 'NO' }}
                        </span>
                        <span v-if="abonado.impresion && abonado.impresion.length > 0" class="tw:text-xs tw:text-gray-400">
                            <i class="fas fa-clock tw:mr-1"></i>
                            Fecha última impresión: {{ abonado.impresion[abonado.impresion.length - 1].fecha_impresion }}
                        </span>
                    </div>
                </div>

                <div class="tw:space-y-2">
                    <p class="tw:text-xs tw:font-semibold tw:text-gray-400 tw:uppercase tw:tracking-wider tw:mb-2 tw:flex tw:items-center tw:gap-1">
                        <i class="fas fa-truck"></i> Envío a domicilio
                    </p>
                    <span
                        :class="abonado.envio ? 'tw:bg-blue-100 tw:text-blue-700' : 'tw:bg-gray-100 tw:text-gray-500'"
                        class="tw:inline-flex tw:items-center tw:text-xs tw:font-semibold tw:rounded-full tw:px-3 tw:py-1"
                    >
                        <i class="fas fa-circle tw:mr-1" style="font-size:7px;vertical-align:middle;"></i>
                        {{ abonado.envio ? 'SI' : 'NO' }}
                    </span>
                    <div v-if="abonado.envio" class="tw:overflow-x-auto tw:rounded-lg tw:border tw:border-gray-200 tw:mt-2">
                        <table class="tw:w-full tw:text-sm">
                            <thead class="tw:bg-gray-50">
                                <tr>
                                    <th
                                        v-for="(val, key) in abonado.envio" :key="key"
                                        class="tw:px-3 tw:py-2 tw:text-left tw:text-xs tw:font-semibold tw:text-gray-500 tw:uppercase tw:tracking-wide"
                                    >{{ key }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr class="tw:bg-white">
                                    <td
                                        v-for="(val, key) in abonado.envio" :key="key"
                                        class="tw:px-3 tw:py-2 tw:text-sm tw:text-gray-700 tw:border-t tw:border-gray-100"
                                    >
                                        <template v-if="key === 'estado'">
                                            <span
                                                :class="val ? 'tw:bg-green-100 tw:text-green-700' : 'tw:bg-red-100 tw:text-red-600'"
                                                class="tw:text-xs tw:font-semibold tw:rounded-full tw:px-2 tw:py-0.5"
                                            >{{ val ? 'ENVIADO' : 'NO ENVIADO' }}</span>
                                        </template>
                                        <template v-else>{{ val }}</template>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div v-if="abonado.segundo_elegido" class="tw:flex tw:items-start tw:gap-2 tw:py-1">
                <i class="fas fa-stopwatch tw:text-gray-300 tw:text-xs tw:mt-0.5 tw:w-4 tw:text-center tw:shrink-0"></i>
                <span class="tw-text-xs tw:text-gray-400 tw:shrink-0" style="min-width:9rem;">El momento seleccionado es el</span>
                <span class="tw-text-xs tw:font-semibold tw:text-gray-800">{{ formatSeg(abonado.segundo_elegido) }}</span>
            </div>

            <template v-if="abonado.propiedades && abonado.propiedades.length > 0">
                <div v-for="campo in abonado.propiedades" :key="campo.id" class="tw:flex tw:items-start tw:gap-2 tw:py-1">
                    <i class="fas fa-puzzle-piece tw:text-gray-300 tw:text-xs tw:mt-0.5 tw:w-4 tw:text-center tw:shrink-0"></i>
                    <span class="tw-text-xs tw:text-gray-400 tw:shrink-0" style="min-width:9rem;">{{ campo.name }}</span>
                    <span class="tw-text-xs tw:font-semibold tw:text-gray-800">{{ campo.value }}</span>
                </div>
            </template>

        </div>
    </div>
</template>

<script setup>
    import { computed } from 'vue'
    import Button from '@/modules/admin/ui/Button.vue'
    import DataField from '@/modules/admin/ui/DataField.vue'
    import OptionsDropdownComponent from '@/modules/admin/ui/OptionsDropdownComponent.vue'

    const props = defineProps({
        abonado: {
            type: Object,
            required: true
        },
        manager: {
            type: Object,
            default: () => ({})
        },
        headerOptions: {
            type: Array,
            default: () => []
        }
    })

    const emit = defineEmits(['actualizar', 'cambiar-asiento', 'cambiar-precio'])

    // Compute vendidoPor based on transaction method
    const vendidoPor = computed(() => {
        if (!props.abonado.transacciones || props.abonado.transacciones.length === 0) {
            return '-'
        }
        const metodo_pago = props.abonado.transacciones[0].metodo_pago
        if (metodo_pago === 5) {
            return 'TPV Online'
        }
        if (props.abonado.vendido_por) {
            const { name, email } = props.abonado.vendido_por
            return `${name || ''} / ${email || ''}`
        }
        return '-'
    })

    function getNombreButaca(butaca) {
        const nButaca = butaca.butaca
        const nFila = butaca.fila
        const sector = butaca.sector_name
        let name = ''
        if (sector) name += `Sector: ${sector} - `
        if (!nButaca && !nFila) {
            name += (butaca.region_name ? butaca.region_name + ' - ' : '') + `Plaza: ${butaca.numero}`
        } else {
            name += (butaca.region_name ? butaca.region_name + ' - ' : '') + `Fila: ${nFila} - Butaca: ${nButaca}`
        }
        return name
    }

    function formatSeg(segundo) {
        const minutes = Math.floor(segundo / 60)
        const remainingSeconds = segundo % 60
        return `Min: ${minutes} - Seg: ${remainingSeconds}`
    }

    // Emit events to let the parent (Show.vue) open modals (props down / events up)
    function openCambiarAsiento() {
        emit('cambiar-asiento')
    }

    function openCambiarPrecio() {
        emit('cambiar-precio')
    }

</script>
